import {
  computed,
  ref,
  shallowReactive,
  shallowRef,
  toValue,
  watch,
  watchEffect,
  type ComputedRef,
  type MaybeRefOrGetter
} from "vue";
import { getEndpointHistory, getSystemHistory } from "../api";
import { BUCKET_SECONDS } from "../series";
import type {
  EndpointPoint,
  EndpointRangeStats,
  SystemMetric,
  SystemPoint,
  TimeRange
} from "../types/api";

export interface HistoryEntry<T> {
  status: "loading" | "ready" | "error";
  /** Bucketed by the API; replaced, never mutated. */
  points: ReadonlyArray<T>;
  /** Seconds each point covers, as the API reports it. */
  resolution: number;
  error: string | null;
  fetchedAt: number;
  /** A newer copy is being fetched while these points stay on screen. */
  refreshing: boolean;
}

export interface EndpointEntry extends HistoryEntry<EndpointPoint> {
  /** Figures for the whole range, when the API provides them. */
  stats: EndpointRangeStats | null;
}

const BLANK = { points: [], resolution: 0, error: null, fetchedAt: 0, refreshing: false } as const;
const LOADING_SYSTEM: HistoryEntry<SystemPoint> = Object.freeze({ status: "loading", ...BLANK });
const LOADING_ENDPOINT: EndpointEntry = Object.freeze({ status: "loading", stats: null, ...BLANK });

/**
 * How long a loaded series counts as current. A chart that stays open is refetched after
 * this long; the payloads are a few kilobytes, so there is no need to patch them in place.
 */
const REFRESH_AFTER_MS: Record<TimeRange, number> = {
  "24h": 5 * 60_000,
  "7d": 15 * 60_000,
  "30d": 30 * 60_000,
  "1y": 60 * 60_000
};

const endpointEntries = shallowReactive<Record<string, EndpointEntry>>({});
const systemEntries = shallowReactive<Record<string, HistoryEntry<SystemPoint>>>({});
/** Bumped on every overview refresh so open charts get a chance to notice they went stale. */
const heartbeat = ref(0);

export function pulseHistory() {
  heartbeat.value += 1;
}

function load<E extends HistoryEntry<unknown>>(
  entries: Record<string, E>,
  key: string,
  blank: E,
  fetchEntry: () => Promise<Omit<E, "status" | "error" | "fetchedAt" | "refreshing">>
) {
  const current = entries[key];
  if (current && (current.status === "loading" || current.refreshing)) return;
  entries[key] = current?.status === "ready" ? { ...current, refreshing: true } : blank;
  void (async () => {
    try {
      const loaded = await fetchEntry();
      entries[key] = {
        ...loaded,
        status: "ready",
        error: null,
        fetchedAt: Date.now(),
        refreshing: false
      } as E;
    } catch (reason) {
      const previous = entries[key];
      entries[key] =
        previous?.status === "ready"
          ? // A failed refresh keeps the older curve; try again after the usual interval.
            { ...previous, refreshing: false, fetchedAt: Date.now() }
          : ({
              ...blank,
              status: "error",
              error: reason instanceof Error ? reason.message : "无法读取历史数据",
              fetchedAt: Date.now()
            } as E);
    }
  })();
}

function isDue(entry: HistoryEntry<unknown> | undefined, range: TimeRange): boolean {
  if (!entry) return true;
  if (entry.status !== "ready" || entry.refreshing) return false;
  return Date.now() - entry.fetchedAt > REFRESH_AFTER_MS[range];
}

const endpointKey = (id: string, range: TimeRange) => `${id}|${range}`;
const systemKey = (metric: SystemMetric, range: TimeRange, step: number) => `${metric}|${range}|${step}`;

function ensureEndpoint(id: string, range: TimeRange) {
  const key = endpointKey(id, range);
  if (!id || !isDue(endpointEntries[key], range)) return;
  load(endpointEntries, key, LOADING_ENDPOINT, async () => {
    const history = await getEndpointHistory(id, range, BUCKET_SECONDS[range]);
    return { points: history.points, resolution: history.resolution, stats: history.stats ?? null };
  });
}

function ensureSystem(metric: SystemMetric, range: TimeRange, step: number) {
  const key = systemKey(metric, range, step);
  if (!isDue(systemEntries[key], range)) return;
  load(systemEntries, key, LOADING_SYSTEM, async () => {
    const history = await getSystemHistory(metric, range, step);
    return { points: history.points, resolution: history.resolution };
  });
}

export function retryEndpointHistory(id: string, range: TimeRange) {
  const key = endpointKey(id, range);
  if (endpointEntries[key]?.status === "error") delete endpointEntries[key];
  ensureEndpoint(id, range);
}

export function retrySystemHistory(metric: SystemMetric, range: TimeRange, step: number) {
  const key = systemKey(metric, range, step);
  if (systemEntries[key]?.status === "error") delete systemEntries[key];
  ensureSystem(metric, range, step);
}

/** Bucketed probe history for one endpoint; fetched on first use and kept reasonably fresh. */
export function useEndpointHistory(
  id: MaybeRefOrGetter<string>,
  range: MaybeRefOrGetter<TimeRange>
): ComputedRef<EndpointEntry> {
  watchEffect(() => {
    void heartbeat.value;
    ensureEndpoint(toValue(id), toValue(range));
  });
  return computed(() => endpointEntries[endpointKey(toValue(id), toValue(range))] ?? LOADING_ENDPOINT);
}

/** Several system metrics over one range and bucket width, in the order given. */
export function useSystemHistory(
  metrics: MaybeRefOrGetter<ReadonlyArray<SystemMetric>>,
  range: MaybeRefOrGetter<TimeRange>,
  step: MaybeRefOrGetter<number>
): ComputedRef<Array<HistoryEntry<SystemPoint>>> {
  watchEffect(() => {
    void heartbeat.value;
    for (const metric of toValue(metrics)) ensureSystem(metric, toValue(range), toValue(step));
  });
  return computed(() =>
    toValue(metrics).map(
      (metric) => systemEntries[systemKey(metric, toValue(range), toValue(step))] ?? LOADING_SYSTEM
    )
  );
}

export interface Settled<T> {
  /** The newest value that finished loading, or null before the first one has. */
  value: T | null;
  /** Something newer is loading; `value` is what was on screen before. */
  pending: boolean;
  error: string | null;
}

/**
 * Hold on to the last loaded result while the next one is on its way, so switching the
 * range dims the old curve instead of collapsing the chart into a placeholder.
 */
export function useSettled<T>(
  source: () => { ready: boolean; error: string | null; value: T }
): ComputedRef<Settled<T>> {
  const last = shallowRef<T | null>(null);
  watch(
    source,
    (current) => {
      if (current.ready) last.value = current.value;
    },
    { immediate: true }
  );
  return computed(() => {
    const current = source();
    if (current.ready) return { value: current.value, pending: false, error: null };
    return { value: last.value, pending: current.error == null, error: current.error };
  });
}
